import { createContext, FC, useContext, useEffect, useState } from "react";
import { supabase } from "./lib/supabaseClient";
import { User } from "@supabase/supabase-js";
import { setAnalyticsAuthenticated } from "./lib/analytics";

export type UserInfo = {
  userId: string;
  name: string;
  isViewClients?: boolean;
  isViewBlogBuilder?: boolean;
  isViewJobs?: boolean;
  isViewDashboard?: boolean;
  isViewNewsletter?: boolean;
  isViewColdEmail?: boolean;
  isViewPerformance?: boolean;
};

type AuthContextType = {
  readonly user?: User;
  readonly setUser?: (value?: User) => void;
  readonly userInfo?: UserInfo;
  readonly isSignInLoading?: boolean;
  readonly isUserInfoLoading?: boolean;
};

const AuthContext = createContext<AuthContextType>({ user: undefined });
export const useAuthContext = () => useContext(AuthContext);

const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | undefined>();
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>();
  const [userInfoLoading, setUserInfoLoading] = useState(false);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data, error } = await supabase.auth.getSession();

      if (!mounted) return;

      if (error) {
        console.error("Failed to get session:", error);
        setUser(undefined);
      } else {
        setUser(data.session?.user ?? undefined);
      }

      setLoading(false);
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Keep this callback very light
      setUser(session?.user ?? undefined);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setAnalyticsAuthenticated(!!user);
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      if (!user?.id) {
        setUserInfo(undefined);
        setUserInfoLoading(false);
        return;
      }

      setUserInfoLoading(true);

      const { data, error } = await supabase
        .from("users")
        .select("userId, name, isViewClients, isViewBlogBuilder, isViewJobs, isViewDashboard, isViewNewsletter, isViewColdEmail, isViewPerformance")
        .eq("userId", user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("Failed to fetch user info:", error);
        setUserInfo(undefined);
        setUserInfoLoading(false);
        return;
      }

      if (!data) {
        setUserInfo(undefined);
        setUserInfoLoading(false);
        return;
      }

      setUserInfo({
        userId: data.userId,
        name: data.name,
        isViewClients: data.isViewClients,
        isViewBlogBuilder: data.isViewBlogBuilder,
        isViewJobs: data.isViewJobs,
        isViewDashboard: data.isViewDashboard,
        isViewNewsletter: data.isViewNewsletter,
        isViewColdEmail: data.isViewColdEmail,
        isViewPerformance: data.isViewPerformance,
      });
      setUserInfoLoading(false);
    };

    fetchUserInfo();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return (
    <AuthContext.Provider
      value={{ user, setUser, isSignInLoading: loading, userInfo, isUserInfoLoading: userInfoLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;